// src/pages/Scheduling.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { IoCalendarOutline, IoTimeOutline, IoCheckmarkDoneOutline, IoPeopleOutline, IoSyncOutline } from 'react-icons/io5';
import image1 from '../assets/images/Calender.png';
import image2 from '../assets/images/Schedule2.png';
import image3 from '../assets/images/ConnectSocial.png';
import image4 from '../assets/images/Messages2.png';
// ✅ Import the new, unique background image for this page
import heroBg from '../assets/images/scheduling-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://oolook.in/" }, { "@type": "ListItem", "position": 2, "name": "Scheduling", "item": "https://oolook.in/scheduling" }]
};
const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Social Media Scheduling",
    "provider": { "@type": "Organization", "name": "Oolook" },
    "name": "AI-Powered Social Media Scheduling & Content Calendar",
    "description": "Oolook's smart scheduling tool helps you visually plan your content calendar, automate posts, and publish at the optimal times for maximum audience engagement.",
    "url": "https://oolook.in/scheduling"
};

// --- Page-Specific Data ---
const schedulingPageData = {
  seo: {
    title: "AI Social Media Scheduler & Content Calendar | Oolook",
    description: "Save hours with Oolook's smart social media scheduler. Visually plan your content, automate posts at the best times, and maintain a consistent brand presence effortlessly.",
    image: 'https://oolook.in/og/scheduling.png',
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoCalendarOutline,
    title: "Effortless Scheduling, Maximum Impact",
    subtitle: "Visually plan your content calendar, automate your publishing schedule, and let Oolook post at the perfect time for every platform.",
    // ✅ Use the new background image here
    backgroundImage: heroBg 
  },
  featureSection: {
    title: "Your Command Center for a Flawless Content Strategy",
    subtitle: "Stop the spreadsheet chaos. Oolook’s smart calendar centralizes your entire publishing workflow, from first draft to final post.",
    features: [
      { title: "Visualize Your Entire Strategy", description: "See all your scheduled content for every platform in one clean, beautiful calendar. Drag and drop to reschedule in seconds.", image: image1 },
      { title: "Post at the Perfect Moment", description: "Let our AI analyze your audience data to find the peak engagement times, then automatically schedule your posts for you.", image: image2 },
      { title: "Publish Everywhere, From One Place", description: "Draft your content once, then customize it for Instagram, X, Facebook, and more. Oolook handles the rest.", image: image3 },
      { title: "Collaborate with Your Team", description: "Approve, comment on, and manage drafts with your team. Ensure every post is perfect before it goes live.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Outcome of Perfect Timing and Consistency",
    benefits: [
      { icon: IoTimeOutline, title: "Reclaim Your Time", description: "Spend minutes, not hours, planning your content calendar for the week or month ahead." },
      { icon: IoSyncOutline, title: "Stay Consistently On-Brand", description: "Maintain an always-on presence across all channels without the daily grind of manual posting." },
      { icon: IoCheckmarkDoneOutline, title: "Eliminate Errors", description: "With a clear approval workflow, you can prevent typos and off-brand content from ever going public." },
      { icon: IoPeopleOutline, title: "Boost Audience Engagement", description: "Reach your followers when they are most active and receptive to your message, driving more interaction." }
    ]
  },
  stepByStep: {
    title: "From Idea to Published in Four Simple Steps",
    subtitle: "See how Oolook turns a complex process into a seamless workflow.",
    steps: [
      { title: "1. Connect Your Accounts", description: "Securely link all your social media profiles to Oolook in under a minute. One connection, endless possibilities." },
      { title: "2. Draft Content with AI", description: "Use our AI generator to create a batch of on-brand posts, or upload your own content to the asset library." },
      { title: "3. Visualize on the Calendar", description: "Drag and drop your drafted posts onto the shared visual calendar. See your entire strategy at a glance." },
      { title: "4. Automate & Analyze", description: "Set your schedule to auto-pilot. Oolook will publish for you and start gathering performance data immediately." }
    ]
  },
  targetAudience: {
    title: "The Perfect Scheduling Tool For..."
  }
};

const Scheduling = () => {
  return (
    <FeaturePageLayout pageData={schedulingPageData} />
  );
};

export default Scheduling;